import { useEffect, useState } from "react";
import { useAuth, formatApiError } from "@/lib/auth";
import { fetchProducts } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { Plus, Pencil, Trash2, X, Calendar, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { resolveImg } from "@/components/admin/ProductsTab";

const EMPTY = {
  title: "", subtitle: "", discount_text: "FLAT 30% OFF", cta_text: "Shop the Offer",
  start_date: "", end_date: "", banner_color: "#FF3366", banner_image: "",
  product_ids: [], is_enabled: true,
};

const COLOR_PRESETS = ["#FF3366", "#CCFF00", "#FF6600", "#9D4CDD", "#33CCFF", "#000000"];

export default function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([
        supabase.from("campaigns").select("*").order("created_at", { ascending: false }).then(r => r.data || []),
        fetchProducts(),
      ]);
      setItems(c);
      setProducts(p);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      const { error } = await supabase.from("campaigns").delete().eq("id", id);
      if (error) throw error;
      toast.success("Campaign deleted");
      load();
    } catch (e) { toast.error(formatApiError(e) || e.message); }
  };

  const isLive = (c) => {
    if (!c.is_enabled) return false;
    const now = new Date();
    return new Date(c.start_date) <= now && new Date(c.end_date) >= now;
  };

  return (
    <div data-testid="admin-campaigns-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-display font-black uppercase text-3xl md:text-4xl">Marketing Campaigns ({items.length})</h2>
          <p className="text-black/70">Schedule offers that auto-show on the landing page during their active window.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-campaign-btn" className="nb-btn bg-[var(--primary)] text-white">
          <Plus className="inline mr-1" size={18} strokeWidth={3} /> New Campaign
        </button>
      </div>

      {items.length === 0 ? (
        <div className="nb-card p-10 text-center">
          <Calendar size={32} strokeWidth={2.5} className="mx-auto mb-3" />
          <h3 className="font-display font-black text-2xl">No campaigns yet</h3>
          <p className="text-black/70">Create your first festive offer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((c) => (
            <div key={c.id} className="nb-card p-5" data-testid={`admin-campaign-${c.id}`} style={{ borderColor: "#000" }}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-black uppercase text-xl">{c.title}</div>
                  <div className="text-sm text-black/70">{c.subtitle}</div>
                </div>
                <span className={`nb-pill ${isLive(c) ? "bg-[var(--secondary)]" : ""}`}>
                  {isLive(c) ? <><CheckCircle2 size={14} strokeWidth={3} className="inline mr-1" />LIVE</> : <><Circle size={14} strokeWidth={3} className="inline mr-1" />SCHEDULED</>}
                </span>
              </div>
              <div className="mt-3 inline-block px-3 py-1 rounded-full text-white font-black uppercase text-sm" style={{ background: c.banner_color }}>
                {c.discount_text}
              </div>
              <div className="text-xs text-black/60 mt-3">
                <b>Start:</b> {fmt(c.start_date)}<br/>
                <b>End:</b> {fmt(c.end_date)}<br/>
                <b>Products:</b> {c.product_ids?.length || 0}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(toForm(c))} className="nb-pill" data-testid={`edit-campaign-${c.id}`}><Pencil size={12} strokeWidth={3} className="inline mr-1" />Edit</button>
                <button onClick={() => remove(c.id)} className="nb-pill bg-black text-white" data-testid={`delete-campaign-${c.id}`}><Trash2 size={12} strokeWidth={3} className="inline mr-1" />Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CampaignForm
          products={products}
          initial={editing}
          busy={busy} setBusy={setBusy}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function toForm(c) {
  return {
    ...c,
    banner_image: c.banner_image || "",
    start_date: toLocal(c.start_date),
    end_date: toLocal(c.end_date),
  };
}
function toLocal(iso) {
  if (!iso) return "";
  // datetime-local needs YYYY-MM-DDTHH:MM
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromForm(f) {
  return {
    title: f.title.trim(),
    subtitle: f.subtitle || "",
    discount_text: f.discount_text || "",
    cta_text: f.cta_text || "Shop the Offer",
    start_date: f.start_date ? new Date(f.start_date).toISOString() : "",
    end_date: f.end_date ? new Date(f.end_date).toISOString() : "",
    banner_color: f.banner_color || "#FF3366",
    banner_image: f.banner_image || null,
    product_ids: f.product_ids || [],
    is_enabled: !!f.is_enabled,
  };
}

function CampaignForm({ products, initial, busy, setBusy, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const isEdit = !!f.id;

  const toggleProduct = (id) => {
    setF((cur) => {
      const ids = new Set(cur.product_ids || []);
      ids.has(id) ? ids.delete(id) : ids.add(id);
      return { ...cur, product_ids: Array.from(ids) };
    });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const ext = file.name.split(".").pop();
      const fileName = `${crypto.randomUUID()}.${ext}`;
      const { data, error } = await supabase.storage.from("images").upload(`campaigns/${fileName}`, file);
      if (error) throw error;
      setF(c => ({ ...c, banner_image: data.path }));
      toast.success("Banner uploaded");
    } catch (e2) { toast.error(formatApiError(e2) || e2.message); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!f.title || !f.discount_text || !f.start_date || !f.end_date) {
      toast.error("Title, discount, start & end dates are required");
      return;
    }
    if (new Date(f.end_date) <= new Date(f.start_date)) {
      toast.error("End date must be after start date");
      return;
    }
    setBusy(true);
    try {
      const body = fromForm(f);
      if (isEdit) {
        const { error } = await supabase.from("campaigns").update(body).eq("id", f.id);
        if (error) throw error;
        toast.success("Campaign updated");
      } else {
        const { error } = await supabase.from("campaigns").insert([body]);
        if (error) throw error;
        toast.success("Campaign created");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="campaign-form-modal">
      <div className="nb-card bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black uppercase text-2xl">{isEdit ? "Edit" : "New"} Campaign</h3>
          <button onClick={onClose} className="nb-pill" data-testid="campaign-form-close"><X size={14} strokeWidth={3} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <L label="Title *">
            <input value={f.title} onChange={e=>setF({...f, title: e.target.value})} placeholder="Diwali Mega Sale" data-testid="camp-title" className="form-input" />
          </L>
          <L label="Discount text *">
            <input value={f.discount_text} onChange={e=>setF({...f, discount_text: e.target.value})} placeholder="FLAT 30% OFF" data-testid="camp-discount" className="form-input" />
          </L>
          <L label="Subtitle / Tagline">
            <input value={f.subtitle} onChange={e=>setF({...f, subtitle: e.target.value})} placeholder="Festival drop — limited time only" data-testid="camp-subtitle" className="form-input" />
          </L>
          <L label="CTA button text">
            <input value={f.cta_text} onChange={e=>setF({...f, cta_text: e.target.value})} data-testid="camp-cta" className="form-input" />
          </L>
          <L label="Start date & time *">
            <input type="datetime-local" value={f.start_date} onChange={e=>setF({...f, start_date: e.target.value})} data-testid="camp-start" className="form-input" />
          </L>
          <L label="End date & time *">
            <input type="datetime-local" value={f.end_date} onChange={e=>setF({...f, end_date: e.target.value})} data-testid="camp-end" className="form-input" />
          </L>
        </div>

        <L label="Banner color">
          <div className="flex gap-2 flex-wrap mt-1">
            {COLOR_PRESETS.map(c => (
              <button key={c} type="button" onClick={() => setF({...f, banner_color: c})}
                data-testid={`camp-color-${c}`}
                className={`w-10 h-10 rounded-full border-[3px] ${f.banner_color === c ? "ring-4 ring-black/30" : ""}`}
                style={{ background: c, borderColor: "#0A0A0A" }} />
            ))}
            <input type="color" value={f.banner_color} onChange={e=>setF({...f, banner_color: e.target.value})} className="w-10 h-10 rounded-full border-[3px] border-black overflow-hidden" />
          </div>
        </L>

        <L label="Banner image (optional)">
          {f.banner_image && (
            <div className="mb-2 relative w-40 h-24 rounded-2xl overflow-hidden border-[3px] border-black">
              <img src={resolveImg(f.banner_image)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setF(c=>({...c, banner_image:""}))} type="button" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center"><X size={12} strokeWidth={3} /></button>
            </div>
          )}
          <label className="nb-pill cursor-pointer inline-block">
            <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="camp-upload" />
            Upload banner
          </label>
        </L>

        <L label="Enable">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={f.is_enabled} onChange={e=>setF({...f, is_enabled: e.target.checked})} data-testid="camp-enabled" className="w-5 h-5" />
            <span>Live during the date window above</span>
          </label>
        </L>

        <L label={`Featured Products in this campaign (${f.product_ids?.length || 0})`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-2 border-[3px] border-black rounded-2xl">
            {products.map(p => (
              <label key={p.id} className={`flex items-center gap-2 p-2 rounded-xl cursor-pointer ${f.product_ids?.includes(p.id) ? "bg-[var(--secondary)]" : "bg-white"}`}>
                <input type="checkbox" checked={f.product_ids?.includes(p.id)} onChange={() => toggleProduct(p.id)} className="w-4 h-4" data-testid={`camp-pick-${p.id}`} />
                <span className="text-xs font-bold truncate">{p.name}</span>
              </label>
            ))}
          </div>
        </L>

        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="nb-pill" data-testid="campaign-cancel">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="campaign-save" className="nb-btn bg-[var(--primary)] text-white disabled:opacity-60">
            {busy ? "Saving…" : (isEdit ? "Update" : "Launch")}
          </button>
        </div>
      </div>

      <style>{`
        .form-input { width:100%; padding: 0.55rem 1rem; border:3px solid #0A0A0A; border-radius: 9999px; box-shadow: 3px 3px 0 0 #0A0A0A; outline:none; background:#fff; }
        .form-input:focus { box-shadow: 4px 4px 0 0 #FF3366; }
      `}</style>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="text-sm font-black uppercase tracking-wider mb-1 inline-block">{label}</span>
      {children}
    </label>
  );
}
